import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import TokenHeader from './TokenHeader';
import SignalStrip from './SignalStrip';
import MarketMetricsPanel from './MarketMetricsPanel';
import ChartPanel from './ChartPanel';
import AIIntelligencePanel from './AIIntelligencePanel';
import OnChainActivityPanel from './OnChainActivityPanel';
import GraphPreviewPanel from './GraphPreviewPanel';
import NarrativePanel from './NarrativePanel';
import NarrativeFlowPanel from './NarrativeFlowPanel';
import MarketStructurePanel from './MarketStructurePanel';
import RelatedTokensPanel from './RelatedTokensPanel';
import TokenScorePanel from './TokenScorePanel';
import ExternalLinksPanel from './ExternalLinksPanel';
import AgentVerdictsTokenPanel from './AgentVerdictsTokenPanel';
import useTokenIntelligenceViewModel from './useTokenIntelligenceViewModel';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 24px',
    maxWidth: 1480,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 8px 18px',
    },
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gap: 10,
    },
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.65fr) minmax(320px, 1fr)',
    gap: 16,
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    [theme.breakpoints.down('sm')]: {
      gap: 10,
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      gap: 10,
    },
  },
  sectionLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 8,
  },
}));

export default function TokenIntelligencePage() {
  const classes = useStyles();
  const { address } = useParams();
  const {
    data,
    loading,
    errors,
    chartRange,
    setChartRange,
    chartMetric,
    setChartMetric,
    chartLoading,
    chartError,
    fallbackActivity,
    intelligence,
    panelLoading,
    panelErrors,
  } = useTokenIntelligenceViewModel(address);

  return (
    <Box className={classes.root}>
      <Box className={classes.stack}>
        <TokenHeader
          address={address}
          metadata={data.metadata}
          liquidity={data.liquidity}
          loading={loading.metadata}
          error={errors.metadata}
        />

        <Box>
          <SignalStrip
            intelligence={intelligence}
            metadata={data.metadata}
            liquidity={data.liquidity}
            stories={data.stories}
            priceHistory={data.priceHistory}
            theses={data.theses}
            activity={fallbackActivity}
            loading={panelLoading.signalStrip}
            error={panelErrors.signalStrip}
            signals={intelligence.signals}
          />
        </Box>

        <Box>
          <TokenScorePanel
            intelligence={intelligence}
            loading={panelLoading.score}
            error={panelErrors.score}
          />
        </Box>

        <Box className={classes.contentGrid}>
          <Box className={classes.column}>
            <ChartPanel
              priceHistory={data.priceHistory}
              liquidityHistory={data.liquidity}
              loading={chartLoading}
              error={chartError}
              range={chartRange}
              metric={chartMetric}
              onRangeChange={setChartRange}
              onMetricChange={setChartMetric}
            />
            <AIIntelligencePanel
              intelligence={intelligence}
              loading={panelLoading.ai}
              error={panelErrors.ai}
            />
            <NarrativePanel
              address={address}
              metadata={data.metadata}
              stories={data.stories}
              theses={data.theses}
              loading={loading.stories || loading.theses}
              error={errors.stories || errors.theses}
            />
            <NarrativeFlowPanel
              address={address}
              metadata={data.metadata}
              flow={data.narrativeFlow}
              loading={panelLoading.narrativeFlow}
              error={panelErrors.narrativeFlow}
            />
            <GraphPreviewPanel
              address={address}
              liquidity={data.liquidity}
              stories={data.stories}
              theses={data.theses}
            />
          </Box>

          <Box className={classes.column}>
            <MarketMetricsPanel
              metadata={data.metadata}
              liquidity={data.liquidity}
              loading={panelLoading.marketMetrics}
              error={panelErrors.marketMetrics}
            />
            <OnChainActivityPanel
              activity={fallbackActivity}
              loading={panelLoading.onChain}
              error={panelErrors.onChain}
            />
            <AgentVerdictsTokenPanel address={address} />
            <MarketStructurePanel
              metadata={data.metadata}
              liquidity={data.liquidity}
            />
            <RelatedTokensPanel
              address={address}
              metadata={data.metadata}
              liquidity={data.liquidity}
              stories={data.stories}
              theses={data.theses}
            />
            <ExternalLinksPanel
              address={address}
              metadata={data.metadata}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
